import { Typography } from '@euler/app/components/typography';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { TroubleLightCell } from '@euler/app/flows/task/pages/pre-inspection/components/TroubleLightCell';
import { useDashboardInspectionTemplate } from '@euler/app/flows/task/pages/pre-inspection/functions/useDashboardInspectionFlow';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { getDefectiveLevelBgColor } from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { DefectiveLevel, InspectionSiteInfo } from '@euler/model';
import {
  AbnormalLevel,
  OptionValueType,
  SeverityLevel,
  SiteInspectionType,
} from '@euler/model/enum';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { nanoid } from 'nanoid/non-secure';
import { useCallback, useEffect, useMemo } from 'react';
import { FlatList, ListRenderItem } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';

const keyExtractor = (site: InspectionSiteInfo) => site.code;

export const DashboardInspectionScreen = wrapNavigatorScreen(
  () => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation<StackNavigationProp<any>>();
    const insets = useSafeAreaInsets();
    const dashboardTemplate = useDashboardInspectionTemplate();
    const { inventoryQuery, orderDetail$, updateDetail, capabilities } =
      useOrderFlowContext();
    const [orderDetail] = useBehaviorSubject(orderDetail$);

    const selectedSiteIds = useMemo(() => {
      return (
        orderDetail.preInspection
          ?.filter(x => x.inspectionType === SiteInspectionType.Dashboard)
          .map(x => x.siteId) ?? []
      );
    }, [orderDetail]);

    const sites = useMemo(() => {
      const list: InspectionSiteInfo[] = [];
      const groups =
        dashboardTemplate?.conf.categories.flatMap(x => x.groups) ?? [];
      const siteIdSet = new Set<number>();
      const disabledSiteIdSet = new Set<number>();
      for (const group of groups) {
        for (const siteId of group.disabledSiteIds ?? []) {
          disabledSiteIdSet.add(siteId);
        }
        for (const siteId of group.siteIds) {
          siteIdSet.add(siteId);
        }
      }
      for (const siteId of siteIdSet) {
        const site = inventoryQuery.getSiteById(siteId);
        if (site != null && !disabledSiteIdSet.has(siteId)) {
          list.push(site);
        }
      }
      return list;
    }, [dashboardTemplate, inventoryQuery]);

    const onItemPress = usePersistFn((siteId: number) => {
      const site = inventoryQuery.getSiteById(siteId);
      if (!site) return;

      const checkItem = site.checkItems[0];
      if (!checkItem) {
        console.error(`Check item of site ${site.name} does not exist`);
        return;
      }

      updateDetail(draft => {
        const index = draft.preInspection?.findIndex(
          x =>
            x.inspectionType === SiteInspectionType.Dashboard &&
            x.siteId === siteId,
        );
        if (index != null && index >= 0) {
          // remove this trouble light result
          draft.preInspection?.splice(index, 1);
        } else {
          // add new trouble light result.
          const abnormalOption = checkItem.options.find(
            x => x.abnormalLevel !== AbnormalLevel.Fine,
          );
          const abnormalLevel =
            abnormalOption?.abnormalLevel ?? AbnormalLevel.Defective;
          const severityLevel =
            abnormalOption?.severityLevel ?? SeverityLevel.Warning;
          const resultDataStringValue = abnormalOption?.label ?? '已点亮';

          if (!draft.preInspection) {
            draft.preInspection = [];
          }

          draft.preInspection.push({
            flowId: '', // not used
            technicianId: 0, // not used
            technicianName: '', // not used
            siteId,
            siteName: site.name,
            siteCode: site.code,
            abnormalLevel,
            severityLevel,
            inspectionType: SiteInspectionType.Dashboard,
            items: [
              {
                _key: nanoid(),
                type: 'item',
                itemId: checkItem.id,
                name: checkItem.name,
                abnormalLevel,
                severityLevel,
                maintenanceAdvice: abnormalOption?.maintenanceAdvice,
                isCustom: false,
                medias: [],
                result: {
                  valueType: OptionValueType.String,
                  value: resultDataStringValue,
                },
              },
            ],
          });
        }
      });
    });

    const renderItem: ListRenderItem<InspectionSiteInfo> = useCallback(
      ({ item }) => {
        return (
          <TroubleLightCell
            siteId={item.id}
            siteCode={item.code}
            name={item.name}
            icon={item.iconUrl}
            checked={selectedSiteIds.includes(item.id)}
            readonly={capabilities?.preInspectionReadonly}
            onPress={onItemPress}
          />
        );
      },
      [onItemPress, selectedSiteIds, capabilities?.preInspectionReadonly],
    );

    useEffect(() => {
      navigation.setOptions({
        title: selectedSiteIds.length
          ? `仪表故障灯 (${selectedSiteIds.length})`
          : '仪表故障灯',
      });
    }, [selectedSiteIds.length, navigation]);

    return (
      <FlatList
        data={sites}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        initialNumToRender={sites.length}
        ListHeaderComponent={
          capabilities?.preInspectionReadonly ? (
            <HStack
              justifyContent="center"
              css={`
                padding: ${rpx(10)}px;
                background-color: ${getDefectiveLevelBgColor(
                  theme,
                  DefectiveLevel.Warning,
                )};
              `}
            >
              <Icon
                name="information-solid"
                color={StatusColors.Warn}
                size={rpx(14)}
              />
              <Typography.Label
                medium
                size={rpx(14)}
                lineHeight={rpx(19)}
                css={`
                  margin-left: ${rpx(5)}px;
                `}
                color={StatusColors.Warn}
              >
                预检已完成，如果要修改预检结果，请前往预检页面。
              </Typography.Label>
            </HStack>
          ) : null
        }
        contentContainerStyle={{
          paddingBottom: insets.bottom + rpx(15),
        }}
        style={{ backgroundColor: '#fff' }}
      />
    );
  },
  {
    title: '仪表故障灯',
  },
);
